import { useNavigate } from 'react-router-dom';
import { KPICard } from '../../components/shared/KPICard';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { useState, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { chartData, STATUS_CONFIG } from '../../data/mockData';
import { fetchCases, fetchActivityFeed, fetchInvoices, fetchPayouts, fetchPenalties } from '../../api';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  FolderKanban,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  IndianRupee,
  Users,
  ArrowRight,
  Calendar,
  AlertTriangle,
  ShieldAlert,
  FileText,
  Wallet,
} from 'lucide-react';
import { format } from 'date-fns';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-lg p-3 text-sm text-slate-700">
        <p className="text-slate-500 mb-1 font-semibold">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { currentRole, currentUserId } = useApp();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [activities, setActivities] = useState([]);
  
  useEffect(() => {
    if (currentRole === ROLES.FIELD_EXECUTIVE || currentRole === ROLES.VENDOR_FE) {
      navigate('/fe-dashboard');
    } else if (currentRole === ROLES.BUSINESS_PARTNER) {
      navigate('/bp-dashboard');
    } else if (currentRole === ROLES.UNIVERSAL_QC) {
      navigate('/qc-dashboard');
    }
  }, [currentRole, navigate]);

  const [stats, setStats] = useState({
    totalCases: 0,
    pendingCases: 0,
    completedCases: 0,
    qcFailed: 0,
    overdueCases: 0,
    penaltyCount: 0,
    invoicePending: 0,
    payoutPending: 0
  });
  
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        const [casesData, activitiesData, invoicesData, payoutsData, penaltiesData] = await Promise.all([
          fetchCases().catch(e => { console.warn(e); return []; }),
          fetchActivityFeed().catch(e => { console.warn(e); return []; }),
          fetchInvoices().catch(e => { console.warn(e); return []; }),
          fetchPayouts().catch(e => { console.warn(e); return []; }),
          fetchPenalties().catch(e => { console.warn(e); return []; })
        ]);
        
        if (!isMounted) return;

        const rawCases = Array.isArray(casesData) ? casesData : [];
        const rawActivities = Array.isArray(activitiesData) ? activitiesData : [];
        const rawInvoices = Array.isArray(invoicesData) ? invoicesData : [];
        const rawPayouts = Array.isArray(payoutsData) ? payoutsData : [];
        const rawPenalties = Array.isArray(penaltiesData) ? penaltiesData : [];

        setCases(rawCases);
        setActivities(rawActivities);

        const pendingInvoices = rawInvoices
          .filter(i => i.status === 'pending' || i.status === 'overdue')
          .reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);

        const pendingPayouts = rawPayouts
          .filter(p => p.status === 'pending')
          .reduce((sum, pay) => sum + (parseFloat(pay.amount) || 0), 0);

        const totalPenalties = rawPenalties.length;
        const overdueCount = rawCases.filter(c => c.status === 'revisit').length;

        if (currentRole === ROLES.FIELD_EXECUTIVE) {
           const feCases = rawCases.filter(c => c.assignedTo_id === currentUserId || c.assignedTo?.id === currentUserId);
           setCases(feCases);
           setActivities(rawActivities.filter(a => a.userId === currentUserId));
           setStats({
             totalCases: feCases.length,
             pendingCases: feCases.filter(c => c.status !== 'completed').length,
             completedCases: feCases.filter(c => c.status === 'completed').length,
             qcFailed: feCases.filter(c => c.status === 'qc_rejected').length,
             overdueCases: feCases.filter(c => c.status === 'revisit').length,
             penaltyCount: 0,
             invoicePending: 0,
             payoutPending: 0
           });
        } else if (currentRole === ROLES.QUALITY_CHECKER) {
           const qcCases = rawCases.filter(c => c.status === 'qc_pending' || c.status === 'qc_rejected');
           setCases(qcCases);
           setActivities(rawActivities.filter(a => a.userId === currentUserId));
           setStats({
             totalCases: qcCases.length,
             pendingCases: qcCases.filter(c => c.status === 'qc_pending').length,
             completedCases: rawCases.filter(c => c.status === 'completed').length,
             qcFailed: qcCases.filter(c => c.status === 'qc_rejected').length,
             overdueCases: 0,
             penaltyCount: 0,
             invoicePending: 0,
             payoutPending: 0
           });
        } else if (currentRole === ROLES.CLIENT) {
           const clientCases = rawCases.filter(c => c.client && (c.client.name === 'HDFC Bank' || c.clientName === 'HDFC Bank'));
           setCases(clientCases);
           setActivities(rawActivities.filter(a => a.userId === currentUserId));
           setStats({
             totalCases: clientCases.length,
             pendingCases: clientCases.filter(c => c.status !== 'completed' && c.status !== 'rejected').length,
             completedCases: clientCases.filter(c => c.status === 'completed').length,
             qcFailed: clientCases.filter(c => c.status === 'rejected' || c.status === 'qc_rejected').length,
             overdueCases: 0,
             penaltyCount: 0,
             invoicePending: 0,
             payoutPending: 0
           });
        } else {
           // Admin, Supreme Manager, Manager, Vendor
           setStats({
             totalCases: rawCases.length,
             pendingCases: rawCases.filter(c => !['completed', 'report_sent'].includes(c.status)).length,
             completedCases: rawCases.filter(c => ['completed', 'report_sent'].includes(c.status)).length,
             qcFailed: rawCases.filter(c => c.status === 'qc_rejected').length,
             overdueCases: overdueCount,
             penaltyCount: totalPenalties,
             invoicePending: pendingInvoices,
             payoutPending: pendingPayouts
           });
        }
      } catch (error) {
        console.error("Dashboard failed to load external data", error);
        if (isMounted) {
          setCases([]);
          setActivities([]);
        }
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [currentRole, currentUserId]);

  const adminKPIs = [
    { title: 'Hierarchy Cases', value: stats.totalCases.toLocaleString(), icon: FolderKanban, iconBg: 'bg-blue-50 border border-blue-100', iconColor: 'text-blue-600', change: stats.totalCases > 0 ? '+12%' : '0%', changeType: 'positive' },
    { title: 'Pending Verifications', value: stats.pendingCases.toLocaleString(), icon: Clock, iconBg: 'bg-amber-50 border border-amber-100', iconColor: 'text-amber-600', change: stats.pendingCases > 0 ? '-5%' : '0%', changeType: 'positive' },
    { title: 'Completed SLA Passes', value: stats.completedCases.toLocaleString(), icon: CheckCircle2, iconBg: 'bg-emerald-50 border border-emerald-100', iconColor: 'text-emerald-600', change: stats.completedCases > 0 ? '+18%' : '0%', changeType: 'positive' },
    { title: 'QC Rejections', value: stats.qcFailed.toLocaleString(), icon: XCircle, iconBg: 'bg-red-50 border border-red-100', iconColor: 'text-red-600', change: stats.qcFailed > 0 ? '+3%' : '0%', changeType: 'negative' },
    { title: 'Overdue Cases', value: stats.overdueCases.toLocaleString(), icon: AlertTriangle, iconBg: 'bg-amber-50 border border-amber-200', iconColor: 'text-amber-700', change: stats.overdueCases > 0 ? 'TAT Alert' : 'On Track', changeType: stats.overdueCases > 0 ? 'negative' : 'positive' },
    { title: 'Auto Penalties Count', value: stats.penaltyCount.toLocaleString(), icon: ShieldAlert, iconBg: 'bg-rose-50 border border-rose-100', iconColor: 'text-rose-600', change: stats.penaltyCount > 0 ? 'Pending Action' : 'Clear', changeType: 'neutral' },
    { title: 'Invoice Pending', value: `₹${stats.invoicePending.toLocaleString()}`, icon: FileText, iconBg: 'bg-violet-50 border border-violet-100', iconColor: 'text-violet-600', change: stats.invoicePending > 0 ? 'Pending' : 'Settled', changeType: 'neutral' },
    { title: 'Payout Pending', value: `₹${stats.payoutPending.toLocaleString()}`, icon: Wallet, iconBg: 'bg-pink-50 border border-pink-100', iconColor: 'text-pink-600', change: stats.payoutPending > 0 ? 'Verify SLA' : 'Settled', changeType: 'positive' },
  ];

  const fieldExecutiveKPIs = [
    { title: 'My Cases Today', value: stats.totalCases.toLocaleString(), icon: FolderKanban, iconBg: 'bg-blue-50 border border-blue-100', iconColor: 'text-blue-600' },
    { title: 'Pending Verification', value: stats.pendingCases.toLocaleString(), icon: Clock, iconBg: 'bg-amber-50 border border-amber-100', iconColor: 'text-amber-600' },
    { title: 'Completed Today', value: stats.completedCases.toLocaleString(), icon: CheckCircle2, iconBg: 'bg-emerald-50 border border-emerald-100', iconColor: 'text-emerald-600' },
    { title: 'Total Earnings', value: `₹${stats.payoutPending.toLocaleString()}`, icon: IndianRupee, iconBg: 'bg-green-55 bg-green-50 border border-green-100', iconColor: 'text-green-600' },
  ];

  const qcKPIs = [
    { title: 'My Total Assigned', value: stats.totalCases.toLocaleString(), icon: FolderKanban, iconBg: 'bg-blue-50 border border-blue-100', iconColor: 'text-blue-600' },
    { title: 'QC Pending', value: stats.pendingCases.toLocaleString(), icon: Clock, iconBg: 'bg-amber-50 border border-amber-100', iconColor: 'text-amber-600' },
    { title: 'QC Completed/Approved', value: stats.completedCases.toLocaleString(), icon: CheckCircle2, iconBg: 'bg-emerald-50 border border-emerald-100', iconColor: 'text-emerald-600' },
    { title: 'Cases Rejected', value: stats.qcFailed.toLocaleString(), icon: XCircle, iconBg: 'bg-red-55 bg-red-50 border border-red-100', iconColor: 'text-red-600' },
  ];

  const vendorKPIs = [
    { title: 'Total Allocated', value: stats.totalCases.toLocaleString(), icon: FolderKanban, iconBg: 'bg-blue-50 border border-blue-100', iconColor: 'text-blue-600' },
    { title: 'Pending Cases', value: stats.pendingCases.toLocaleString(), icon: Clock, iconBg: 'bg-amber-50 border border-amber-100', iconColor: 'text-amber-600' },
    { title: 'Completed by FEs', value: stats.completedCases.toLocaleString(), icon: CheckCircle2, iconBg: 'bg-emerald-50 border border-emerald-100', iconColor: 'text-emerald-600' },
    { title: 'Active Penalties', value: stats.penaltyCount.toLocaleString(), icon: Users, iconBg: 'bg-indigo-50 border border-indigo-100', iconColor: 'text-indigo-600' },
  ];

  const clientKPIs = [
    { title: 'My Total Cases', value: stats.totalCases.toLocaleString(), icon: FolderKanban, iconBg: 'bg-blue-50 border border-blue-100', iconColor: 'text-blue-600' },
    { title: 'In Progress', value: stats.pendingCases.toLocaleString(), icon: Clock, iconBg: 'bg-amber-50 border border-amber-100', iconColor: 'text-amber-600' },
    { title: 'Successfully Completed', value: stats.completedCases.toLocaleString(), icon: CheckCircle2, iconBg: 'bg-emerald-50 border border-emerald-100', iconColor: 'text-emerald-600' },
    { title: 'Rejected/Issues', value: stats.qcFailed.toLocaleString(), icon: XCircle, iconBg: 'bg-red-50 border border-red-100', iconColor: 'text-red-600' },
  ];

  const displayKPIs = currentRole === ROLES.FIELD_EXECUTIVE ? fieldExecutiveKPIs : 
                      currentRole === ROLES.QUALITY_CHECKER ? qcKPIs : 
                      currentRole === ROLES.VENDOR ? vendorKPIs : 
                      currentRole === ROLES.CLIENT ? clientKPIs : adminKPIs;

  const casesByStatus = [
    { status: 'Completed', value: stats.completedCases, color: '#10b981' },
    { status: 'In Progress', value: stats.pendingCases, color: '#f59e0b' },
    { status: 'QC Rejected', value: stats.qcFailed, color: '#dc2626' },
    { status: 'Revisit', value: stats.overdueCases, color: '#ef4444' },
  ];

  const emptyMonths = ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'];
  const caseTrends = cases.length > 0 ? chartData.caseTrends : emptyMonths.map(m => ({ month: m, cases: 0, completed: 0 }));
  const revenueVsPayouts = (stats.invoicePending > 0 || stats.payoutPending > 0) ? chartData.revenueVsPayouts : emptyMonths.map(m => ({ month: m, revenue: 0, payouts: 0 }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, here's what's happening today.</p>
        </div>
        <div className="flex items-center gap-3">
          {currentRole === ROLES.CLIENT && (
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/20 px-6">
              + New Lead / Case
            </Button>
          )}
          <Button variant="outline" className="bg-white border-slate-200 text-slate-700 hover:text-slate-900 hover:bg-slate-50 gap-2">
            <Calendar className="w-4 h-4" />
            {format(new Date(), 'MMM dd, yyyy')}
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div 
        className={`grid grid-cols-1 md:grid-cols-2 ${
          currentRole === ROLES.ADMIN ? 'lg:grid-cols-4' : 'lg:grid-cols-4'
        } gap-4`} 
        data-testid="kpi-grid"
      >
        {displayKPIs.map((kpi) => (
          <KPICard key={kpi.title} {...kpi} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Case Trends Chart */}
        <div className="crm-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-900">Case Trends</h3>
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 font-semibold">
              View All
            </Button>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={caseTrends}>
                <defs>
                  <linearGradient id="colorCases" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="cases"
                  stroke="#6366f1"
                  fillOpacity={1}
                  fill="url(#colorCases)"
                  name="Total Cases"
                />
                <Area
                  type="monotone"
                  dataKey="completed"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorCompleted)"
                  name="Completed"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue vs Payouts Chart */}
        <div className="crm-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-900">Revenue vs Payouts</h3>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-indigo-500" style={{ backgroundColor: '#6366f1' }} />
                <span className="text-slate-600 font-semibold">Revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-emerald-500" />
                <span className="text-slate-600 font-semibold">Payouts</span>
              </div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueVsPayouts}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} tickFormatter={(value) => `₹${(value/100000).toFixed(0)}L`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} name="Revenue" />
                <Bar dataKey="payouts" fill="#10b981" radius={[4, 4, 0, 0]} name="Payouts" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cases by Status */}
        <div className="crm-card p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Cases by Status</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={casesByStatus}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {casesByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {casesByStatus.map((item) => (
              <div key={item.status} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-xs text-slate-600 font-medium">{item.status}</span>
                <span className="text-xs text-slate-900 font-semibold ml-auto">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Cases */}
        <div className="crm-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Recent Cases</h3>
            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-slate-50 gap-1 font-semibold">
              View All <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <ScrollArea className="h-64">
            <div className="space-y-3">
              {cases.slice(0, 5).map((caseItem) => (
                <div
                  key={caseItem.id}
                  className="flex flex-col p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-slate-100/80 cursor-pointer transition-colors"
                  data-testid={`recent-case-${caseItem.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{caseItem.refNumber}</p>
                      <p className="text-xs text-slate-500 truncate">{caseItem.applicantName}</p>
                    </div>
                    <StatusBadge
                      status={STATUS_CONFIG[caseItem.status]?.color || 'neutral'}
                      label={STATUS_CONFIG[caseItem.status]?.label || caseItem.status}
                      size="sm"
                      showIcon={false}
                    />
                  </div>
                  {currentRole === ROLES.FIELD_EXECUTIVE && (
                    <div className="mt-3 pt-2 border-t border-slate-100 flex justify-end">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-[10px] uppercase font-bold border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/extra-costs');
                        }}
                      >
                        Apply for Extra Cost
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {cases.length === 0 && <p className="text-sm text-slate-400 p-2">No recent cases found.</p>}
            </div>
          </ScrollArea>
        </div>

        {/* Activity Feed */}
        <div className="crm-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Activity Feed</h3>
            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-slate-50 gap-1 font-semibold">
              View All <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <ScrollArea className="h-64">
            <div className="space-y-4">
              {activities.map((activity) => (
                <div key={activity.id} className="flex gap-3" data-testid={`activity-${activity.id}`}>
                  <Avatar className="w-8 h-8 flex-shrink-0 border border-slate-200">
                    <AvatarImage src={activity.avatar} />
                    <AvatarFallback>{activity.userName?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800">
                      <span className="font-semibold">{activity.action}</span>
                    </p>
                    <p className="text-xs text-slate-500 truncate">{activity.description}</p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {format(new Date(activity.timestamp), 'MMM dd, hh:mm a')}
                    </p>
                  </div>
                </div>
              ))}
              {activities.length === 0 && <p className="text-sm text-slate-400 p-2">No recent activities found.</p>}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
